import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Eye, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { DraftPreview } from "./DraftPreview";
import { useOutlook } from "./OutlookCard";

const canEmail = (c) => Boolean(c.email) && !c.unsubscribed_at;

const RecipientRow = ({ c, checked, onToggle }) => {
  const disabled = !canEmail(c);
  const badge = c.unsubscribed_at ? "Unsubscribed" : !c.email ? "No email" : c.followup_sent_at ? "Emailed" : null;
  return (
    <label className={`flex items-center gap-3 rounded-xl bg-white border border-black/10 px-3 py-2.5 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <input type="checkbox" disabled={disabled} checked={checked} onChange={onToggle} data-testid={`followup-recipient-${c.id}`} className="h-5 w-5 accent-brand-teal shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-[13.5px] text-brand-charcoal truncate">{c.name || c.email || c.phone}</span>
        <span className="block text-[11.5px] text-brand-charcoal/55 truncate">{c.email || "—"}</span>
      </span>
      {badge && <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/50 shrink-0">{badge}</span>}
    </label>
  );
};

const PreviewView = ({ preview, count, sending, onBack, onSend }) => (
  <>
    <div className="flex-1 overflow-y-auto px-4 pb-28" data-testid="followup-preview">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55">Subject</p>
      <p data-testid="followup-preview-subject" className="font-bold text-brand-charcoal mb-3">{preview.subject}</p>
      <iframe title="Email preview" srcDoc={preview.html} sandbox="" data-testid="followup-preview-frame" className="w-full h-[70vh] rounded-2xl border border-black/10 bg-white" />
    </div>
    <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-black/10 p-3">
      <div className="max-w-lg mx-auto grid grid-cols-[auto_1fr] gap-2">
        <button type="button" onClick={onBack} data-testid="followup-back" className="h-12 px-5 rounded-full border border-black/15 font-bold text-[12px] uppercase tracking-[0.1em] inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Edit
        </button>
        <button type="button" onClick={onSend} disabled={sending} data-testid="followup-approve-send" className="h-12 rounded-full bg-brand-teal text-white font-bold text-[12.5px] uppercase tracking-[0.1em] inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Approve & send to {count}
        </button>
      </div>
    </div>
  </>
);

// Compose → pick recipients → preview → Approve & Send.
export const FollowUpSheet = ({ event, contacts, onSent, onClose }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [picked, setPicked] = useState(() => new Set(contacts.filter((c) => canEmail(c) && !c.followup_sent_at).map((c) => c.id)));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("draft");
  const { status: outlook } = useOutlook();

  useEffect(() => {
    api.get(`/admin/events/${event.id}/followup`)
      .then(({ data }) => { setSubject(data.subject); setBody(data.body); })
      .catch(() => toast.error("Couldn't load the email template. Close and try again."));
  }, [event.id]);

  const emailable = useMemo(() => contacts.filter(canEmail), [contacts]);
  const ids = [...picked];
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setAll = (on) => setPicked(new Set(on ? emailable.map((c) => c.id) : []));

  const doPreview = async () => {
    if (channel === "draft") {
      setPreview({ kind: "draft" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/followup/preview`, { subject, body, contact_ids: ids });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't build the preview.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!window.confirm(`Send this email to ${ids.length} contact${ids.length === 1 ? "" : "s"} now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/followup/send`, { subject, body, contact_ids: ids });
      toast.success(`Sending to ${data.queued} contact${data.queued === 1 ? "" : "s"}…`);
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="followup-sheet" className="fixed inset-0 z-50 bg-brand-cream flex flex-col">
      <div className="max-w-lg w-full mx-auto px-4 pt-4 pb-3 flex items-center justify-between">
        <p className="font-display font-black text-2xl text-brand-charcoal">{preview ? (preview.kind === "draft" ? "Your draft" : "Preview") : "Thank-you email"}</p>
        <button type="button" onClick={onClose} data-testid="followup-close" className="p-2 rounded-full bg-white border border-black/10" aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      {preview?.kind === "draft" ? (
        <div className="max-w-lg w-full mx-auto flex-1 flex flex-col min-h-0">
          <DraftPreview
            event={event}
            subject={subject}
            body={body}
            recipients={contacts.filter((c) => picked.has(c.id) && canEmail(c))}
            outlook={outlook}
            onBack={() => setPreview(null)}
            onDone={onSent}
          />
        </div>
      ) : preview ? (
        <div className="max-w-lg w-full mx-auto flex-1 flex flex-col min-h-0">
          <PreviewView preview={preview} count={ids.length} sending={busy} onBack={() => setPreview(null)} onSend={send} />
        </div>
      ) : (
        <>
          <div className="max-w-lg w-full mx-auto flex-1 overflow-y-auto px-4 pb-28 space-y-3">
            <div className="grid grid-cols-2 gap-1 rounded-full bg-white border border-black/10 p-1" role="radiogroup" data-testid="followup-channel">
              {[["draft", "Draft in my email"], ["designed", "Designed email"]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={channel === key}
                  onClick={() => setChannel(key)}
                  data-testid={`followup-channel-${key}`}
                  className={`h-10 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] ${channel === key ? "bg-brand-charcoal text-white" : "text-brand-charcoal/60"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11.5px] text-brand-charcoal/55">
              {channel === "draft"
                ? "Opens a plain email from your own account with everyone in BCC. You press send."
                : "We send a branded email from info@btwnyc.com, one per person."}
            </p>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="followup-subject" className="h-12 rounded-xl bg-white border-black/10 text-[16px]" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} data-testid="followup-body" className="rounded-xl bg-white border-black/10 text-[15px] leading-relaxed" />
            <p className="text-[11.5px] text-brand-charcoal/55">
              <b>{"{first_name}"}</b> and <b>{"{event_name}"}</b> are filled in for each person{channel === "draft" ? " (a BCC draft says \"there\" instead of a name)" : ""}. Menu and Request a Quote links plus website links are added automatically.
            </p>
            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55" data-testid="followup-picked-count">
                Send to {ids.length} of {emailable.length}
              </p>
              <div className="flex gap-3 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-teal">
                <button type="button" onClick={() => setAll(true)} data-testid="followup-select-all">All</button>
                <button type="button" onClick={() => setAll(false)} data-testid="followup-select-none">None</button>
              </div>
            </div>
            <div className="space-y-2" data-testid="followup-recipients">
              {contacts.map((c) => <RecipientRow key={c.id} c={c} checked={picked.has(c.id)} onToggle={() => toggle(c.id)} />)}
            </div>
          </div>
          <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-black/10 p-3">
            <div className="max-w-lg mx-auto">
              <button type="button" onClick={doPreview} disabled={busy || !ids.length || !subject.trim() || !body.trim()} data-testid="followup-preview-button" className="w-full h-12 rounded-full bg-brand-charcoal text-white font-bold text-[12.5px] uppercase tracking-[0.1em] inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} {channel === "draft" ? "Preview draft" : "Preview email"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FollowUpSheet;
